import { memo, useCallback, useEffect, useMemo } from 'react';

import { useAuthUser } from 'hooks/useAuthUser';
import userService from 'services/user';
import { HYPERFLOW_CHAT_BLACK_ID, HYPERFLOW_CHAT_ELITE_ID, HYPERFLOW_CHAT_UNITY_ID, HYPERFLOW_FLOW_ID } from 'settings';

import Layout from '@eduzz/ui-layout';
import { type CurrentUser } from '@eduzz/ui-layout/Topbar/HyperflowSupportChat/types';

import { createSupportChatAccountsJwt, useHyperflowJwtQuery } from '../hooks';

const { Topbar } = Layout;
const { HyperflowSupportChat } = Topbar;

function SupportChat() {
  const { data: user } = useAuthUser();

  const isProducer = user?.is_producer;

  const userToHyperflow = useMemo((): CurrentUser => {
    const isAccessPolicy =
      user?.acpo_original_user_cli_cod !== undefined && user?.acpo_original_user_cli_cod !== user?.cli_cod;

    const baseUser = {
      id: user?.cli_cod,
      name: user?.user_name,
      email: user?.user_email,
      belt: !user?.belt_id ? 'White' : userService.getBeltById(user?.belt_id),
      tag: !user?.is_producer ? null : user?.is_clube_black ? 'unity' : 'pro',
      isClubeBlack: user?.is_clube_black,
      isAccessPolicy
    };

    if (isAccessPolicy) {
      return {
        ...baseUser,
        originalUserId: user?.acpo_original_user_cli_cod,
        originalUserName: user?.acpo_original_user_name,
        originalUserEmail: user?.acpo_original_user_email
      } as CurrentUser;
    }

    return baseUser as CurrentUser;
  }, [user]);

  const configToHyperflow = {
    chatUnityID: HYPERFLOW_CHAT_UNITY_ID,
    chatBlackID: HYPERFLOW_CHAT_BLACK_ID,
    chatEliteID: HYPERFLOW_CHAT_ELITE_ID,
    flowId: HYPERFLOW_FLOW_ID,
    origin: 'blinket'
  } as const;

  const hyperflowJwtQuery = useHyperflowJwtQuery(userToHyperflow);

  const createAccountsJwtPromise = useCallback(() => {
    if (!user?.ssid) {
      return Promise.reject(new Error('No ssid'));
    }

    return createSupportChatAccountsJwt(user.ssid);
  }, [user?.ssid]);

  useEffect(() => {
    return () => {
      if (typeof Hyperflow !== 'undefined') {
        Hyperflow.clear();
      }
    };
  }, []);

  const shouldShow = !!isProducer && !hyperflowJwtQuery.isLoading && !!hyperflowJwtQuery.data;

  return (
    <>
      {shouldShow && (
        <HyperflowSupportChat
          accountsJwt={createAccountsJwtPromise}
          currentUser={userToHyperflow}
          hyperflowConfig={configToHyperflow}
          hyperflowJwt={hyperflowJwtQuery.data}
        />
      )}
    </>
  );
}

export default memo(SupportChat);
